import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Message } from '../types';
import { sendMessage, extractPlaylistFromResponse } from '../services/openaiService';
import './ChatInterface.css';

const ChatInterface: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingFinalPlaylist, setIsGeneratingFinalPlaylist] = useState(false);
  const [showPlaylistConfirmation, setShowPlaylistConfirmation] = useState(false);
  const [currentPlaylist, setCurrentPlaylist] = useState<any>(null);
  const [showFullScreenPlaylist, setShowFullScreenPlaylist] = useState(false);
  const [isPartyMode, setIsPartyMode] = useState(false);
  const [currentBackground, setCurrentBackground] = useState('default');
  const [previousBackground, setPreviousBackground] = useState('default');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showPlaylistSidebar, setShowPlaylistSidebar] = useState(false);
  const [showExportPopup, setShowExportPopup] = useState(false);
  const [showSpotifyExportPopup, setShowSpotifyExportPopup] = useState(false);
  const [spotifyExportStatus, setSpotifyExportStatus] = useState<{
    type: 'loading' | 'success' | 'error';
    title: string;
    message: string;
    playlistUrl?: string;
  } | null>(null);
  const [appleMusicExportStatus, setAppleMusicExportStatus] = useState<{
    type: 'loading' | 'success' | 'error';
    title: string;
    message: string;
    playlistUrl?: string;
  } | null>(null);
  const [spotifyConnected, setSpotifyConnected] = useState(false);
  const [spotifyUser, setSpotifyUser] = useState<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const callCountRef = useRef(0);
  const backgroundIndexRef = useRef(0);
  const stateRestoredRef = useRef(false);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const focusTextarea = useCallback(() => {
    textareaRef.current?.focus();
  }, []);

  // iOS Safari input focus handler
  const handleInputFocus = useCallback(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    
    if (isIOS && isSafari) {
      console.log('📱 Input focused on iOS Safari, ensuring visibility');
      // Force the input container to be visible
      setTimeout(() => {
        const inputContainer = document.querySelector('.input-container, .centered-input-container');
        if (inputContainer) {
          (inputContainer as HTMLElement).style.position = 'fixed';
          (inputContainer as HTMLElement).style.bottom = '0';
          (inputContainer as HTMLElement).style.left = '0';
          (inputContainer as HTMLElement).style.right = '0';
          (inputContainer as HTMLElement).style.zIndex = '9999';
          (inputContainer as HTMLElement).style.background = 'rgba(255, 255, 255, 0.95)';
          (inputContainer as HTMLElement).style.backdropFilter = 'blur(10px)';
          (inputContainer as HTMLElement).style.borderTop = '1px solid rgba(0, 0, 0, 0.1)';
        }
      }, 100);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Debug initial load
  useEffect(() => {
    console.log('🚀 Component loaded with initial background:', currentBackground);
    console.log('Initial default overlay opacity:', currentBackground === 'default' ? 1 : 0);
    console.log('Initial special overlay opacity:', currentBackground !== 'default' ? 1 : 0);
  }, []); // Empty dependency array = runs only on mount

  // Debug render state changes
  useEffect(() => {
    console.log('🔍 Render state changed:', {
      messagesLength: messages.length,
      showFullScreenPlaylist,
      currentPlaylist: !!currentPlaylist,
      isLoading,
      layout: messages.length === 0 ? 'initial' : 'chat'
    });
  }, [messages.length, showFullScreenPlaylist, currentPlaylist, isLoading]);

  // iOS Safari viewport height fix
  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    
    if (isIOS && isSafari) {
      console.log('📱 iOS Safari detected, applying viewport fixes');
      
      // Function to update viewport height
      const updateViewportHeight = () => {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
        console.log('📱 Updated viewport height:', vh);
      };
      
      // Set initial viewport height
      updateViewportHeight();
      
      // Update on resize (virtual keyboard show/hide)
      window.addEventListener('resize', updateViewportHeight);
      window.addEventListener('orientationchange', updateViewportHeight);
      
                      // Also update when input is focused (virtual keyboard appears)
        const handleTextareaFocus = () => {
          updateViewportHeight();
          handleInputFocus();
        };
        
        const textareas = document.querySelectorAll('textarea');
        textareas.forEach(textarea => {
          textarea.addEventListener('focus', handleTextareaFocus);
          textarea.addEventListener('blur', updateViewportHeight);
        });
        
        return () => {
          window.removeEventListener('resize', updateViewportHeight);
          window.removeEventListener('orientationchange', updateViewportHeight);
          textareas.forEach(textarea => {
            textarea.removeEventListener('focus', handleTextareaFocus);
            textarea.removeEventListener('blur', updateViewportHeight);
          });
        };
    }
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    console.log('📝 URL parameters:', Object.fromEntries(urlParams.entries()));
    
    const success = urlParams.get('success');
    const method = urlParams.get('method');
    const error = urlParams.get('error');
    
    // Handle OAuth return from cookie-based flow
    if (success === 'true' && method === 'cookie') {
      console.log('✅ Spotify OAuth flow completed successfully');
      
      // Get playlist data from sessionStorage (works across tabs)
      let playlistToExport: any = null;
      const storedPlaylist = sessionStorage.getItem('pendingSpotifyPlaylist');
      if (storedPlaylist) {
        try {
          playlistToExport = JSON.parse(storedPlaylist);
          console.log('🎵 Found playlist data in sessionStorage:', playlistToExport.name);
          console.log('📋 Playlist data retrieved:', {
            name: playlistToExport.name,
            songCount: playlistToExport.songs.length,
            songs: playlistToExport.songs.map((s: any) => `${s.name} by ${s.artist}`)
          });
          console.log('🔍 Raw playlist data:', playlistToExport);
          // Clear the stored data
          sessionStorage.removeItem('pendingSpotifyPlaylist');
        } catch (error) {
          console.error('❌ Error parsing playlist data from sessionStorage:', error);
        }
      }
      
      // Restore conversation state from sessionStorage
      const storedConversation = sessionStorage.getItem('pendingConversationState');
      if (storedConversation && !stateRestoredRef.current) {
        try {
          const conversationState = JSON.parse(storedConversation);
          console.log('🔄 Restoring conversation state from sessionStorage...');
          
          // Fix timestamps - convert string timestamps back to Date objects
          const fixedMessages = (conversationState.messages || []).map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }));
          
          setMessages(fixedMessages);
          setCurrentPlaylist(conversationState.currentPlaylist || null);
          setInputValue(conversationState.inputValue || '');
          stateRestoredRef.current = true;
          
          console.log('✅ Conversation state restored from sessionStorage');
          sessionStorage.removeItem('pendingConversationState');
        } catch (error) {
          console.error('❌ Error restoring conversation state:', error);
        }
      }
      
      // Clear the URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      console.log('🔄 URL parameters cleared');
      
      // Export playlist if we have one
      if (playlistToExport) {
        console.log('🎵 Proceeding with playlist export...');
        setTimeout(async () => {
          const connectionResult = await checkSpotifyConnection();
          if (connectionResult.connected) {
            handleSpotifyExport(connectionResult, playlistToExport);
          } else {
            console.log('❌ Not connected to Spotify after OAuth return');
          }
        }, 1000);
      } else if (currentPlaylist) {
        console.log('🎵 Using current playlist for export...');
        setTimeout(async () => {
          const connectionResult = await checkSpotifyConnection();
          if (connectionResult.connected) {
            handleSpotifyExport(connectionResult, currentPlaylist);
          } else {
            console.log('❌ Not connected to Spotify after OAuth return');
          }
        }, 1000);
      } else {
        console.log('❌ No playlist found after OAuth return');
      }
    } else if (error) {
      console.log('❌ OAuth error:', error, urlParams.get('message'));
      // Clear the URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [currentPlaylist]);

  useEffect(() => {
    // Focus textarea when loading state changes (response completes)
    if (!isLoading) {
      // Small delay to ensure the response has been rendered
      setTimeout(focusTextarea, 100);
    }
  }, [isLoading, focusTextarea]);

  // Log playlist data when it changes
  useEffect(() => {
    if (currentPlaylist) {
      console.log('Playlist updated with', currentPlaylist.songs.length, 'songs');
      currentPlaylist.songs.forEach((song: any, index: number) => {
        console.log(`Song ${index + 1}:`, song.title);
      });
    }
  }, [currentPlaylist]);

  // Debug party mode changes
  useEffect(() => {
    console.log('🎨 Background changed to:', currentBackground);
    console.log('🎨 Is default?', currentBackground === 'default');
    console.log('🎨 Default overlay opacity:', currentBackground === 'default' ? 1 : 0);
    console.log('🎨 Special overlay opacity:', currentBackground !== 'default' ? 1 : 0);
    console.log('🎨 Special overlay background color:', getBackgroundColor(currentBackground));
    
    // Check if overlays exist in DOM
    setTimeout(() => {
      const defaultOverlay = document.querySelector('.default-overlay');
      const specialOverlay = document.querySelector('.special-overlay');
      
      if (defaultOverlay) {
        const defaultStyle = window.getComputedStyle(defaultOverlay as HTMLElement);
        console.log('🎨 Default overlay in DOM:', {
          opacity: defaultStyle.opacity,
          background: defaultStyle.background,
          zIndex: defaultStyle.zIndex,
          position: defaultStyle.position
        });
      } else {
        console.log('❌ Default overlay not found in DOM');
      }
      
      if (specialOverlay) {
        const specialStyle = window.getComputedStyle(specialOverlay as HTMLElement);
        console.log('🎨 Special overlay in DOM:', {
          opacity: specialStyle.opacity,
          background: specialStyle.background,
          zIndex: specialStyle.zIndex,
          position: specialStyle.position
        });
      } else {
        console.log('❌ Special overlay not found in DOM');
      }
    }, 100);
  }, [currentBackground]);

  // Simple function to toggle party mode
  const togglePartyMode = () => {
    console.log('Button clicked! Current background:', currentBackground);
    
    // Cycle through different background types for testing
    const backgroundTypes = ['default', 'party', 'chill', 'workout', 'studying', 'romantic'];
    const currentIndex = backgroundTypes.indexOf(currentBackground);
    const nextIndex = (currentIndex + 1) % backgroundTypes.length;
    const nextBackground = backgroundTypes[nextIndex];
    
    // Handle smooth transitions
    if (currentBackground !== 'default' && nextBackground !== 'default') {
      // Transition between two special backgrounds
      setPreviousBackground(currentBackground);
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentBackground(nextBackground);
        setIsTransitioning(false);
      }, 3000); // Full 3s transition
    } else {
      // Direct transition to/from default
      setCurrentBackground(nextBackground);
    }
    
    console.log('New background will be:', nextBackground);
  };

  // Function to get background color based on type
  const getBackgroundColor = (backgroundType: string) => {
    // Define vibrant, obvious colors for testing
    const backgrounds = [
      'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)', // Vibrant red-orange
      'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)', // Bright teal
      'linear-gradient(135deg, #45b7d1 0%, #96c93d 100%)', // Blue to green
      'linear-gradient(135deg, #f9ca24 0%, #f0932b 100%)', // Bright yellow-orange
      'linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%)', // Purple to light purple
      'linear-gradient(135deg, #fd79a8 0%, #fdcb6e 100%)', // Pink to yellow
      'linear-gradient(135deg, #00b894 0%, #00cec9 100%)', // Green to cyan
      'linear-gradient(135deg, #e84393 0%, #fd79a8 100%)', // Magenta to pink
      'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)', // Light blue to blue
      'linear-gradient(135deg, #a29bfe 0%, #6c5ce7 100%)', // Light purple to purple
    ];
    
    // If it's a number (index), use it directly
    if (!isNaN(Number(backgroundType))) {
      const index = Number(backgroundType) % backgrounds.length;
      return backgrounds[index];
    }
    
    // Default to first background
    return backgrounds[0];
  };

  const formatMessage = (content: string) => {
    // Remove any JSON from the end of the content for display (including backticks)
    let cleanContent = content.trim();
    
    // Remove markdown code blocks if present
    if (cleanContent.includes('```json')) {
      cleanContent = cleanContent.replace(/```json\s*\{[\s\S]*?\}\s*```/g, '');
    } else if (cleanContent.includes('```')) {
      cleanContent = cleanContent.replace(/```\s*\{[\s\S]*?\}\s*```/g, '');
    } else {
      // Fallback: remove JSON without backticks (anywhere in content)
      cleanContent = cleanContent.replace(/\s*\{[\s\S]*?\}\s*/g, '');
    }
    
    // If there's no content after removing JSON, it was a pure JSON response
    if (!cleanContent.trim()) {
      return (
        <div>
          <p className="message-paragraph">
            <strong>🎵 Your playlist has been generated successfully!</strong>
          </p>
          <p className="message-paragraph">
            Check out your personalized playlist below! 🎉
          </p>
        </div>
      );
    }
    
    // Split by double line breaks to preserve paragraphs
    const paragraphs = cleanContent.split('\n\n');
    
    return paragraphs.map((paragraph, index) => {
      // Handle bullet points and numbered lists
      if (paragraph.trim().startsWith('•') || paragraph.trim().startsWith('-')) {
        const items = paragraph.split('\n').filter(item => item.trim());
        return (
          <ul key={index} className="message-list">
            {items.map((item, itemIndex) => (
              <li key={itemIndex}>{item.replace(/^[•-]\s*/, '')}</li>
            ))}
          </ul>
        );
      }
      
      // Handle numbered lists
      if (/^\d+\./.test(paragraph.trim())) {
        const items = paragraph.split('\n').filter(item => item.trim());
        return (
          <ol key={index} className="message-list">
            {items.map((item, itemIndex) => (
              <li key={itemIndex}>{item.replace(/^\d+\.\s*/, '')}</li>
            ))}
          </ol>
        );
      }
      
      // Handle bold text (text between **)
      const formattedParagraph = paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      
      // Handle italic text (text between *)
      const finalParagraph = formattedParagraph.replace(/\*(.*?)\*/g, '<em>$1</em>');
      
      return (
        <p 
          key={index} 
          className="message-paragraph"
          dangerouslySetInnerHTML={{ __html: finalParagraph }}
        />
      );
    });
  };

  const handleSendMessage = async () => {
    callCountRef.current += 1;
    if (!inputValue.trim() || isLoading) {
      return;
    }
    
    // Prevent multiple simultaneous calls
    if (isLoading) {
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue.trim(),
      role: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Start timing the AI response
    const startTime = Date.now();
    console.log('⏱️ Starting AI request at:', new Date().toLocaleTimeString());

    try {
      const allMessages = [...messages, userMessage];
      
      const response = await sendMessage(allMessages);
      
      // Calculate and log response time
      const endTime = Date.now();
      const responseTime = endTime - startTime;
      console.log(`⏱️ AI Response Time: ${responseTime}ms (${(responseTime / 1000).toFixed(2)}s)`);
      console.log(`⏱️ Response received at: ${new Date().toLocaleTimeString()}`);
      
      // Log the raw AI response to see what's being sent back
      console.log('=== RAW AI RESPONSE ===');
      console.log(response);
      console.log('=== END RAW RESPONSE ===');
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: response,
        role: 'assistant',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Check if the response contains background information
      let responseData = null;
      try {
        responseData = extractPlaylistFromResponse(response);
        console.log('=== EXTRACTED JSON DATA ===');
        console.log(responseData);
        console.log('=== END EXTRACTED DATA ===');
      } catch (error) {
        console.error('Error in extractPlaylistFromResponse:', error);
      }
      
      // Always cycle through background colors on every AI response
      setIsTransitioning(true);
      backgroundIndexRef.current = (backgroundIndexRef.current + 1) % 10; // 10 backgrounds total
      console.log('🎨 Cycling to background:', backgroundIndexRef.current);
      
      // Delay setting the new background until transition completes
      setTimeout(() => {
        setCurrentBackground(backgroundIndexRef.current.toString());
        setIsTransitioning(false);
      }, 2000);
      
      if (responseData) {
        // If it's a final playlist, log it
        if (responseData.playlist) {
          setCurrentPlaylist(responseData.playlist);
          setShowFullScreenPlaylist(true);
          setIsGeneratingFinalPlaylist(false);
        } else if (responseData.ready_to_create) {
          // GPT is ready to create the final playlist
          setShowPlaylistConfirmation(true);
        }
      }
      
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: 'Sorry, I encountered an error. Please try again.',
        role: 'assistant',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsGeneratingFinalPlaylist(false);
      setShowPlaylistConfirmation(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProceedToGenerate = async () => {
    setShowPlaylistConfirmation(false);
    setIsGeneratingFinalPlaylist(true);
    setIsLoading(true);
    
    // Create the user message
    const userMessage: Message = {
      id: Date.now().toString(),
      content: "Yes, please create my final playlist now with all the songs we discussed.",
      role: 'user',
      timestamp: new Date(),
    };
    
    // Add the user message to the messages array
    setMessages(prev => [...prev, userMessage]);
    
    console.log('Sending proceed message to AI:', userMessage.content);
    
    // Start timing the final playlist generation
    const startTime = Date.now();
    console.log('⏱️ Starting final playlist generation at:', new Date().toLocaleTimeString());
    
    // Small delay to ensure loading state is visible
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      const finalResponse = await sendMessage([...messages, userMessage]);
      
      // Calculate and log final playlist generation time
      const endTime = Date.now();
      const responseTime = endTime - startTime;
      console.log(`⏱️ Final Playlist Generation Time: ${responseTime}ms (${(responseTime / 1000).toFixed(2)}s)`);
      console.log(`⏱️ Final playlist received at: ${new Date().toLocaleTimeString()}`);
      
      console.log('=== FINAL PLAYLIST RAW RESPONSE ===');
      console.log(finalResponse);
      console.log('=== END FINAL PLAYLIST RESPONSE ===');
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: finalResponse,
        role: 'assistant',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // Process the final playlist
      let responseData = null;
      try {
        responseData = extractPlaylistFromResponse(finalResponse);
        console.log('=== FINAL PLAYLIST EXTRACTED DATA ===');
        console.log(responseData);
        console.log('=== END FINAL PLAYLIST DATA ===');
      } catch (error) {
        console.error('Error in extractPlaylistFromResponse:', error);
      }
      
      if (responseData && responseData.playlist) {
        console.log('Final playlist generated successfully!');
        setCurrentPlaylist(responseData.playlist);
        setShowFullScreenPlaylist(true);
        setIsGeneratingFinalPlaylist(false);
        setIsLoading(false);
      } else {
        // If no playlist was generated, show error
        console.error('No playlist was generated in the response');
        console.log('Response was:', finalResponse);
        setIsGeneratingFinalPlaylist(false);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error generating final playlist:', error);
      setIsGeneratingFinalPlaylist(false);
      setIsLoading(false);
    }
  };

  const handleContinueEditing = () => {
    setShowPlaylistConfirmation(false);
  };

  const handleExportClick = () => {
    setShowExportPopup(true);
    // Auto-hide after 3 seconds
    setTimeout(() => setShowExportPopup(false), 3000);
  };

  const handleSpotifyExport = async (connectionResult?: { connected: boolean; user: any }, playlistData?: any) => {
    console.log('🎵 handleSpotifyExport called');
    console.log('📋 Current playlist:', currentPlaylist ? 'Present' : 'Missing');
    console.log('🔗 Connection result:', connectionResult?.connected);
    console.log('🔗 Spotify connected (state):', spotifyConnected);
    console.log('🔗 Spotify user:', spotifyUser);
    
    // Check if we have a playlist from passed data, current state, or localStorage
    let playlistToExport = playlistData || currentPlaylist;
    if (!playlistToExport) {
      // Try to get playlist from localStorage (for OAuth return scenarios)
      const pendingState = localStorage.getItem('pendingConversationState');
      if (pendingState) {
        try {
          const conversationState = JSON.parse(pendingState);
          playlistToExport = conversationState.currentPlaylist;
          console.log('📋 Found playlist in localStorage:', playlistToExport ? 'Present' : 'Missing');
        } catch (error) {
          console.error('❌ Error parsing pending conversation state:', error);
        }
      }
    }
    
    if (!playlistToExport) {
      console.log('❌ No playlist to export');
      return;
    }

    console.log('🔄 Redirecting to Spotify OAuth...');

    // Check if we're connected to Spotify (use passed connection result or fallback to state)
    const isConnected = connectionResult?.connected || spotifyConnected || spotifyUser;
    console.log('🔗 Connection check - connectionResult:', connectionResult?.connected, 'spotifyConnected:', spotifyConnected, 'spotifyUser:', !!spotifyUser, 'isConnected:', isConnected);

    if (!isConnected) {
      console.log('❌ Not connected to Spotify, redirecting to OAuth...');
      
      // Store playlist data in sessionStorage for cross-tab compatibility
      console.log('🔍 Original playlist structure:', playlistToExport);
      console.log('🔍 First song structure:', playlistToExport.songs[0]);
      
      const playlistData = {
        name: playlistToExport.name,
        description: playlistToExport.description,
        songs: playlistToExport.songs.map((song: any) => ({
          name: song.title || song.name,
          artist: song.artist
        }))
      };
      
      // Store conversation state in sessionStorage
      const conversationState = {
        messages,
        currentPlaylist: playlistToExport,
        inputValue
      };
      
      sessionStorage.setItem('pendingSpotifyPlaylist', JSON.stringify(playlistData));
      sessionStorage.setItem('pendingConversationState', JSON.stringify(conversationState));
      console.log('💾 Stored playlist and conversation data in sessionStorage for OAuth');
      console.log('📋 Playlist data stored:', {
        name: playlistData.name,
        songCount: playlistData.songs.length,
        songs: playlistData.songs.map((s: any) => `${s.name} by ${s.artist}`)
      });
      
      window.location.href = `/api/auth/spotify/login-cookie?returnUrl=${encodeURIComponent(window.location.pathname)}`;
      return;
    }

    console.log('🎵 Exporting playlist to Spotify...');
    setShowSpotifyExportPopup(true);
    setSpotifyExportStatus({
      type: 'loading',
      title: 'Creating Playlist...',
      message: 'Please wait while we create your playlist in Spotify.'
    });

    try {
      // Prepare playlist data
      const playlistData = {
        name: playlistToExport.name,
        description: playlistToExport.description,
        songs: playlistToExport.songs.map((song: any) => ({
          name: song.title || song.name,
          artist: song.artist
        }))
      };

      console.log('📤 Sending playlist to Spotify:', playlistData);
      
      // Tokens are now in HTTP-only cookies, so we don't need to send them in the body
      const response = await fetch('/api/spotify/create-playlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(playlistData)
      });

      console.log('📥 Spotify response:', response.status);

      // Token refresh is now handled automatically by the cookie-based endpoint

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Playlist created successfully:', result);
        
        console.log('✅ Playlist created successfully');
        
        // Update popup to show success
        setSpotifyExportStatus({
          type: 'success',
          title: 'Playlist Created!',
          message: `"${playlistData.name}" has been created successfully with ${result.songs_added} songs.`,
          playlistUrl: result.playlist_url
        });
      } else {
        const error = await response.text();
        console.error('❌ Failed to create playlist:', error);
        
        // Check if it's an expired token error
        if (response.status === 401 || error.includes('expired') || error.includes('unauthorized')) {
          console.log('🔄 Token appears to be expired, redirecting to OAuth...');
          setShowSpotifyExportPopup(false);
          setSpotifyExportStatus(null);
          
          // Clear the expired connection state
          setSpotifyConnected(false);
          setSpotifyUser(null);
          
          // Store playlist data and redirect to OAuth
          const playlistData = {
            name: currentPlaylist.name,
            description: currentPlaylist.description,
            songs: currentPlaylist.songs.map((song: any) => ({
              name: song.title,
              artist: song.artist,
              album: song.album || 'Unknown Album'
            }))
          };
          sessionStorage.setItem('pendingSpotifyPlaylist', JSON.stringify(playlistData));
          
          handleSpotifyLogin();
          return;
        }
        
        // Update popup to show error for other types of failures
        setSpotifyExportStatus({
          type: 'error',
          title: 'Export Failed',
          message: `Failed to create playlist: ${error}`
        });
      }
          } catch (error) {
        console.error('❌ Error exporting playlist:', error);
      
      // Check if it's a network error that might indicate expired tokens
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('unauthorized') || errorMessage.includes('401')) {
        console.log('🔄 Network error suggests expired tokens, redirecting to OAuth...');
        setShowSpotifyExportPopup(false);
        setSpotifyExportStatus(null);
        
        // Clear the expired connection state
        setSpotifyConnected(false);
        setSpotifyUser(null);
        
        // Store playlist data and redirect to OAuth
        const playlistData = {
          name: currentPlaylist.name,
          description: currentPlaylist.description,
          songs: currentPlaylist.songs.map((song: any) => ({
            name: song.title,
            artist: song.artist,
            album: song.album || 'Unknown Album'
          }))
        };
        sessionStorage.setItem('pendingSpotifyPlaylist', JSON.stringify(playlistData));
        
        handleSpotifyLogin();
        return;
      }
      
      // Update popup to show error for other types of failures
      setSpotifyExportStatus({
        type: 'error',
        title: 'Export Failed',
        message: `Error creating playlist: ${errorMessage}`
      });
    }
  };

  const handleSpotifyLogin = () => {
    console.log('🔗 Initiating Spotify login (cookie-based)...');
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    console.log('🔄 Return URL will be:', returnUrl);
    console.log('🔄 Current location:', window.location.href);
    window.location.href = `/api/auth/spotify/login-cookie?returnUrl=${returnUrl}`;
  };

    const checkSpotifyConnection = async () => {
    try {
      console.log('🔍 Checking Spotify connection (cookie-based)...');
      
      // Return early if we're already connected
      if (spotifyConnected && spotifyUser) {
        console.log('✅ Already connected to Spotify, skipping check');
        return { connected: true, user: spotifyUser };
      }
      
      // Use cookie-based authentication
      const response = await fetch('/api/auth/spotify/status-cookie');
      console.log('📊 Cookie status response:', response.status, response.statusText);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📦 Response data:', data);
        
        if (data.connected) {
          console.log('✅ Spotify connected via cookies:', data.user);
          setSpotifyConnected(true);
          setSpotifyUser(data.user);
          return { connected: true, user: data.user };
        } else {
          console.log('❌ Not connected to Spotify');
          setSpotifyConnected(false);
          setSpotifyUser(null);
          return { connected: false, user: null };
        }
              } else {
          console.log('❌ Status check failed');
          setSpotifyConnected(false);
          setSpotifyUser(null);
          return { connected: false, user: null };
        }
      } catch (error) {
        console.error('❌ Error checking Spotify connection:', error);
        setSpotifyConnected(false);
        setSpotifyUser(null);
        return { connected: false, user: null };
      }
    };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="chat-container">
      {/* Background Overlays */}
      <div 
        className={`background-overlay default-overlay ${currentBackground === 'default' ? 'active' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: `url('/musiciqbackground.png') center center / cover no-repeat`,
          opacity: currentBackground === 'default' ? 1 : 0,
          transition: 'opacity 3s ease-in-out',
          pointerEvents: 'none',
          zIndex: -1
        }}
      />
      <div 
        className={`background-overlay special-overlay-1 ${currentBackground !== 'default' && !isTransitioning ? 'active' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: `${getBackgroundColor(currentBackground)}, url('/musiciqbackground.png') center center / cover no-repeat`,
          backgroundBlendMode: 'overlay',
          opacity: currentBackground !== 'default' && !isTransitioning ? 0.7 : 0,
          transition: 'opacity 1s ease-in-out',
          pointerEvents: 'none',
          zIndex: -1
        }}
      />
      <div 
        className={`background-overlay special-overlay-2 ${isTransitioning ? 'active' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: `${getBackgroundColor(backgroundIndexRef.current.toString())}, url('/musiciqbackground.png') center center / cover no-repeat`,
          backgroundBlendMode: 'overlay',
          opacity: isTransitioning ? 0.7 : 0,
          transition: 'opacity 2s ease-in-out',
          pointerEvents: 'none',
          zIndex: -1
        }}
      />
      
      {showFullScreenPlaylist && currentPlaylist ? (
        // Full-screen playlist view
        <div className="fullscreen-playlist">
          <div className="playlist-header">
            <h1>{currentPlaylist.name}</h1>
            <p className="playlist-description">{currentPlaylist.description}</p>
            <p className="song-count">{currentPlaylist.songs.length} songs</p>
          </div>
          
          {/* Top action buttons */}
          <div className="playlist-actions">
            <button 
              className="continue-editing-button"
              onClick={() => setShowFullScreenPlaylist(false)}
            >
              Continue Editing
            </button>
            <button 
              className="spotify-button-fullscreen connected" 
              onClick={() => {
                console.log('🔘 Spotify button clicked! (fullscreen)');
                handleSpotifyExport();
              }}
            >
              Export to Spotify
            </button>
            <button className="apple-music-button-fullscreen" onClick={handleExportClick}>
              Export to Apple Music
            </button>
          </div>
          
          <div className="playlist-songs-fullscreen">
            <div className="songs-list-fullscreen">
              {currentPlaylist.songs.map((song: any, index: number) => (
                <div key={index} className="song-item-fullscreen">
                  <span className="song-number-fullscreen">{index + 1}.</span>
                  <div className="song-details-fullscreen">
                    <span className="song-title-fullscreen">{song.title}</span>
                    <span className="song-artist-fullscreen">{song.artist}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Bottom action buttons */}
          <div className="playlist-actions">
            <button 
              className="continue-editing-button"
              onClick={() => setShowFullScreenPlaylist(false)}
            >
              Continue Editing
            </button>
            <button 
              className="spotify-button-fullscreen connected" 
              onClick={() => {
                console.log('🔘 Spotify button clicked! (bottom)');
                handleSpotifyExport();
              }}
            >
              Export to Spotify
            </button>
            <button className="apple-music-button-fullscreen" onClick={handleExportClick}>
              Export to Apple Music
            </button>
          </div>
        </div>
      ) : messages.length === 0 ? (
        // Initial state with centered layout
        <div className="initial-layout">
          <div className="welcome-message">
            <h1>MusicIQ</h1>
            <p>Hey there! What kind of playlist would you like to create today? (e.g., pool party, workout, studying, relaxing, road trip)</p>
          </div>
          
          <div className="centered-input-container">
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              onFocus={handleInputFocus}
              placeholder="Message..."
              disabled={isLoading}
              rows={1}
            />
            <button
              onClick={() => {
                handleSendMessage();
              }}
              disabled={!inputValue.trim() || isLoading}
              className="send-button"
            >
              {isLoading ? 'Sending...' : 'Send'}
            </button>
          </div>
          
          
        </div>
      ) : (
        // Chat state with messages and sidebar
        <div className={`chat-layout ${!currentPlaylist ? 'no-playlist' : ''}`}>
          <div className="chat-main">
            <div className="chat-header">
              <h1>MusicIQ</h1>
              {currentPlaylist && !showFullScreenPlaylist && (
                <button 
                  className="toggle-playlist-button"
                  onClick={() => {
                    // On mobile, go directly to fullscreen instead of sidebar
                    if (window.innerWidth <= 768) {
                      setShowFullScreenPlaylist(true);
                    } else {
                      setShowPlaylistSidebar(!showPlaylistSidebar);
                    }
                  }}
                >
                  {window.innerWidth <= 768 ? 'View Playlist' : (showPlaylistSidebar ? 'Hide Playlist' : 'Show Playlist')}
                </button>
              )}
            </div>
            
            <div className="messages-container">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`message ${message.role === 'user' ? 'user-message' : 'assistant-message'}`}
                >
                  <div className="message-content">
                    {message.role === 'assistant' ? formatMessage(message.content) : message.content}
                  </div>
                  <div className="message-timestamp">
                    {message.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="message assistant-message">
                  <div className="message-content">
                    <div className="typing-indicator">
                      {isGeneratingFinalPlaylist ? (
                        <div className="final-playlist-loading">
                          <div className="loading-text">🎵 Generating your final playlist...</div>
                          <div className="loading-subtext">This may take a minute to create the perfect mix for you!</div>
                          <div className="loading-dots">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                        </div>
                      ) : (
                        <>
                          <span></span>
                          <span></span>
                          <span></span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
            
            {showPlaylistConfirmation && (
              <div className="playlist-confirmation-overlay">
                <div className="playlist-confirmation-card">
                  <div className="confirmation-header">
                    <h3>🎵 Ready to Create Your Playlist!</h3>
                    <p>I think I have enough information to generate your custom list. This will take a few moments to create. Would you like me to proceed or do you have additional feedback?</p>
                  </div>
                  <div className="confirmation-buttons">
                    <button 
                      className="proceed-button"
                      onClick={handleProceedToGenerate}
                    >
                      🚀 Proceed
                    </button>
                    <button 
                      className="continue-editing-button-confirmation"
                      onClick={handleContinueEditing}
                    >
                      ✏️ Continue Editing
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="input-container">
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                onFocus={handleInputFocus}
                placeholder="Message..."
                disabled={isLoading}
                rows={1}
              />
              <button
                onClick={() => {
                  handleSendMessage();
                }}
                disabled={!inputValue.trim() || isLoading}
                className="send-button"
              >
                {isLoading ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
          
          {/* Playlist Sidebar - Hidden on mobile */}
          {currentPlaylist && window.innerWidth > 768 && (
            <div className={`playlist-sidebar ${showPlaylistSidebar ? 'show' : ''}`}>
              <div className="playlist-sidebar-header">
                <h3>{currentPlaylist.name}</h3>
                <p>{currentPlaylist.description}</p>
                <p className="song-count">{currentPlaylist.songs.length} songs</p>
              </div>
              
              <div className="playlist-sidebar-songs">
                <div className="songs-list-sidebar">
                  {currentPlaylist.songs.map((song: any, index: number) => (
                    <div key={index} className="song-item-sidebar">
                      <span className="song-number-sidebar">{index + 1}.</span>
                      <div className="song-details-sidebar">
                        <span className="song-title-sidebar">{song.title}</span>
                        <span className="song-artist-sidebar">{song.artist}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="playlist-sidebar-actions">
                <button 
                  className="view-fullscreen-button"
                  onClick={() => setShowFullScreenPlaylist(true)}
                >
                  View Full Screen
                </button>
                <button 
                  className="spotify-button connected" 
                  onClick={() => {
                    console.log('🔘 Spotify button clicked! (sidebar)');
                    handleSpotifyExport();
                  }}
                >
                  Export to Spotify
                </button>
                <button className="apple-music-button" onClick={handleExportClick}>
                  Export to Apple Music
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Export Popup */}
      {showExportPopup && (
        <div className="export-popup-overlay">
          <div className="export-popup-card">
            <div className="export-popup-content">
              <h3>🚀 Exporting Coming Soon!</h3>
              <p>We're working hard to bring you seamless playlist export to your favorite music platforms.</p>
              <div className="export-popup-icons">
                <span className="export-icon spotify-icon">🎵</span>
                <span className="export-icon apple-icon">🍎</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Spotify Export Popup */}
      {showSpotifyExportPopup && (
        <div className="export-popup-overlay">
          <div className="export-popup-card">
            <div className="export-popup-content">
              <h3>{spotifyExportStatus?.title || '🎵 Exporting to Spotify...'}</h3>
              <p>{spotifyExportStatus?.message || 'Creating your playlist and adding songs. This may take a moment.'}</p>
              <div className="export-popup-icons">
                <span className="export-icon spotify-icon">🎵</span>
                {spotifyExportStatus?.type === 'loading' && <div className="loading-spinner"></div>}
              </div>
              {spotifyExportStatus?.type === 'success' && (
                <div className="export-popup-actions">
                  {spotifyExportStatus.playlistUrl && (
                    <a 
                      href={spotifyExportStatus.playlistUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="view-playlist-button"
                    >
                      View in Spotify
                    </a>
                  )}
                  <button 
                    onClick={() => {
                      setShowSpotifyExportPopup(false);
                      setSpotifyExportStatus(null);
                    }}
                    className="ok-button"
                  >
                    OK
                  </button>
                </div>
              )}
              {spotifyExportStatus?.type === 'error' && (
                <div className="export-popup-actions">
                  <button 
                    onClick={() => {
                      setShowSpotifyExportPopup(false);
                      setSpotifyExportStatus(null);
                    }}
                    className="ok-button"
                  >
                    OK
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatInterface; 